import { apiClient } from '@/core/api/client';

/** Feature 095 — Longevity Readiness Assessment (advisor send & track). */

export type LongevityAssessmentStatus = 'INVITED' | 'IN_PROGRESS' | 'COMPLETED';
export type LongevityReadinessCategory =
  | 'HIGHLY_PREPARED'
  | 'WELL_POSITIONED'
  | 'BUILDING_READINESS'
  | 'PLANNING_OPPORTUNITY';

export type AssessmentDeliveryMode = 'EMAIL' | 'LINK';

/**
 * Which question set to send. Opt-in per send rather than a global switch, so
 * MVP 2 can be exercised without handing the new questions to every live client
 * at once. Omitted means V1.
 */
export type QuestionSetVersion = '1' | '2';

export type SendAssessmentRequest = {
  clientName: string;
  clientEmail: string;
  /** EMAIL sends the invitation email; LINK only returns a copyable link (no email). */
  deliveryMode: AssessmentDeliveryMode;
  questionSetVersion?: QuestionSetVersion;
};

export type AssessmentLinkResponse = {
  id: string;
  linkToken: string;
  clientName: string;
  clientEmail: string;
  status: LongevityAssessmentStatus;
  expiresAt: string;
  lastSentAt: string;
  isResend: boolean;
};

/**
 * One client card in the advisor's Longevity Assessments section.
 * `overallScore`/`category` are present ONLY for shared completed results —
 * the backend omits them for private results, so the UI never has a score to leak.
 */
export type AssessmentCard = {
  id: string;
  clientName: string;
  clientEmail: string;
  status: LongevityAssessmentStatus;
  isPrivate: boolean;
  /** Which report the advisor opens — MVP 2 has its own advisor view, V1 does not. */
  questionSetVersion: QuestionSetVersion;
  overallScore?: number;
  category?: LongevityReadinessCategory;
  /** Completed pillars; present only while IN_PROGRESS. */
  pillarsAnswered?: number;
  /** How many pillars this version has — six in V1, five in MVP 2. */
  pillarCount?: number;
  completedAt?: string | null;
  createdAt: string;
};

export type AdvisorAssessments = {
  stats: {
    sent: number;
    completed: number;
    averageReadiness: number | null;
  };
  clients: AssessmentCard[];
};

/**
 * One of the advisor's own test runs (Feature 118).
 *
 * Unlike AssessmentCard the score is never withheld: that card hides it until the
 * client shares, and here the advisor IS the client, so there is nobody whose
 * privacy the withholding would protect.
 */
export type SelfTest = {
  id: string;
  /** The advisor opens the take flow with this, exactly as a client would. */
  linkToken: string;
  status: LongevityAssessmentStatus;
  questionSetVersion: QuestionSetVersion;
  createdAt: string;
  completedAt: string | null;
  overallScore: number | null;
  category: LongevityReadinessCategory | null;
};

/** Send (or resend) a longevity assessment link. 429 on resend cooldown. */
export async function sendAssessment(data: SendAssessmentRequest): Promise<AssessmentLinkResponse> {
  const response = await apiClient.post<AssessmentLinkResponse>('/longevity-assessments', data);
  return response.data;
}

/** Fetch the advisor's Longevity Assessments section (stat strip + client cards). */
export async function fetchAdvisorAssessments(): Promise<AdvisorAssessments> {
  const response = await apiClient.get<AdvisorAssessments>('/longevity-assessments');
  return response.data;
}

/**
 * Start a test run as yourself. Always creates a new one — no resend, no cooldown
 * and no limit on how many may be unfinished at once, unlike sending to a client.
 */
export async function createSelfTest(questionSetVersion: QuestionSetVersion): Promise<SelfTest> {
  const response = await apiClient.post<SelfTest>('/longevity-assessments/self-tests', {
    questionSetVersion,
  });
  return response.data;
}

/** The advisor's own test runs, newest first. Never mixed into the client list. */
export async function fetchSelfTests(): Promise<SelfTest[]> {
  const response = await apiClient.get<SelfTest[]>('/longevity-assessments/self-tests');
  return response.data;
}
